/**
 * Payload for PUT /api/Controller/{interfaceId}/{id}.
 *
 * The request replaces the decoder row, so every field it owns has to be sent
 * back rather than only the edited one — sending literal zeros for the switch
 * parameters wiped the inrush / holding voltage configured on the device.
 *
 * Shared by the decoder grid (inline + bulk save) and the bulk apply
 * configuration run, which write the same record through different UI.
 */
export const buildControllerUpdatePayload = (
  row: any,
  fallbackInterfaceId: number,
  ids: { configuration: number; decoderType: number | null },
) => ({
  // A decoder on a control hub belongs to a sub-controller, so it must be
  // written back to its own interface, not the hub's. The row carries that
  // original interface id; fall back to the current interface for a plain
  // (non-hub) controller.
  interfaceId: Number(row.interfaceId) || fallbackInterfaceId,
  address: row.address,
  id: row.id,
  name: row.name,
  decoderType: ids.decoderType,
  configuration: ids.configuration,
  verify: row.verify ?? false,
  force: row.force ?? false,
  inrushVoltage: row.inrushVoltage ?? 0,
  inrushTime: row.inrushTime ?? 0,
  holdingVoltage: row.holdingVoltage ?? 0,
  overrideSwitchParams: row.overrideSwitchParams ?? false,
  overrideOptions: row.overrideOptions ?? false,
  twiId: row.twiId,
})

/**
 * The decoder's purpose id.
 *
 * Input and sdi-12 decoders have no purpose, and the API writes this straight
 * onto the column, so a 0 here would replace their null.
 */
export const resolveDecoderTypeId = (
  ...candidates: Array<number | string | undefined | null>
): number | null => {
  for (const candidate of candidates) {
    // Number(undefined) is NaN, which `??` does not catch.
    const id = Number(candidate)
    if (Number.isFinite(id) && id > 0) return id
  }
  return null
}
